<script setup lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { useFlowEditorContext } from '../composables/useFlowEditor'
import CanvasSettingsPanel from './inspector/CanvasSettingsPanel.vue'
import NodeSettingsPanel from './inspector/NodeSettingsPanel.vue'
import ShortcutsPanel from './inspector/ShortcutsPanel.vue'
import TemplatesPanel from './inspector/TemplatesPanel.vue'
import VisualSettingsPanel from './inspector/VisualSettingsPanel.vue'

/** Collapsible left panel showing the panel chosen in the toolbar. */
const { inspectorPanel, inspectorCollapsed } = useFlowEditorContext().ui
</script>

<template>
  <aside
    class="flow-inspector flow-settings"
    :class="{ panel: !inspectorCollapsed, 'flow-inspector--collapsed': inspectorCollapsed }"
  >
    <!-- Expanded: normal header with title + collapse button -->
    <header v-if="!inspectorCollapsed" class="panel__header flow-inspector__header">
      <h2 v-if="inspectorPanel === 'flow'">{{ $t('flow.nodeSettings') }}</h2>
      <h2 v-else-if="inspectorPanel === 'templates'">{{ $t('flow.templates') }}</h2>
      <h2 v-else-if="inspectorPanel === 'canvas'">{{ $t('flow.canvasSettings') }}</h2>
      <h2 v-else-if="inspectorPanel === 'visual'">{{ $t('flow.visualSettings') }}</h2>
      <h2 v-else>{{ $t('flow.shortcuts') }}</h2>
      <button
        type="button"
        class="panel-collapse-btn panel-collapse-btn--side"
        :title="$t('flow.collapsePanel')"
        @click="inspectorCollapsed = true"
      >
        <ChevronLeft style="width: 0.9rem; height: 0.9rem" aria-hidden="true" />
      </button>
    </header>
    <!-- Collapsed: only the expand button -->
    <div v-else class="inspector-collapsed-strip">
      <button
        type="button"
        class="panel-collapse-btn"
        :title="$t('flow.expandPanel')"
        @click="inspectorCollapsed = false"
      >
        <ChevronRight style="width: 0.9rem; height: 0.9rem" aria-hidden="true" />
      </button>
    </div>

    <div v-show="!inspectorCollapsed" class="panel__body flow-settings__body">
      <NodeSettingsPanel v-if="inspectorPanel === 'flow'" />
      <TemplatesPanel v-else-if="inspectorPanel === 'templates'" />
      <CanvasSettingsPanel v-else-if="inspectorPanel === 'canvas'" />
      <VisualSettingsPanel v-else-if="inspectorPanel === 'visual'" />
      <ShortcutsPanel v-else />
    </div>
  </aside>
</template>

<style scoped src="./styles/panelCollapse.css"></style>

<style scoped>
.flow-inspector__header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.flow-inspector--collapsed {
  overflow: hidden;
  min-width: 0;
}

.inspector-collapsed-strip {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0 0 0;
  flex: 0 0 auto;
}

.flow-settings__body {
  overflow: auto;
  min-height: 0;
}
</style>
